export const COMPLIANCE_STATUS_KEYS = [
  "Complied",
  "Partial Complied",
  "Not Applicable",
  "Not Complied",
  "Not Updated",
];

export const COMPLIANCE_STATUS_KEYS_FOR_BARS = [
  "Complied",
  "Partial Complied",
  "Not Applicable",
  "Not Complied",
];

export function normalizeComplianceStatus(status) {
  if (status == null || String(status).trim() === "") {
    return "Not Updated";
  }

  const normalized = String(status).trim().toLowerCase();

  if (normalized === "complied" || normalized === "compiled") {
    return "Complied";
  }

  if (
    normalized === "partial complied" ||
    normalized === "partial compiled" ||
    normalized === "complied with exception" ||
    normalized === "compiled with exception"
  ) {
    return "Partial Complied";
  }

  if (normalized === "not applicable") {
    return "Not Applicable";
  }

  if (normalized === "not complied" || normalized === "not compiled") {
    return "Not Complied";
  }

  if (normalized === "not updated") {
    return "Not Updated";
  }

  const canonical = COMPLIANCE_STATUS_KEYS.find(
    (key) => key.toLowerCase() === normalized
  );

  return canonical || String(status).trim();
}

/** Local calendar day — avoids UTC shift from toISOString() (Jul 1 IST → Jun 30). */
export function startOfLocalDay(value) {
  if (!value) return null;
  const date = value instanceof Date ? new Date(value.getTime()) : new Date(value);
  if (isNaN(date.getTime())) return null;
  date.setHours(0, 0, 0, 0);
  return date;
}

export function endOfLocalDay(value) {
  const date = startOfLocalDay(value);
  if (!date) return null;
  date.setHours(23, 59, 59, 999);
  return date;
}

export function formatLocalYmd(value) {
  const date = value instanceof Date ? value : new Date(value);
  if (isNaN(date.getTime())) return "";
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

export function parseLocalYmd(value) {
  if (!value) return null;
  const match = String(value).trim().match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (match) {
    return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  }
  return startOfLocalDay(value);
}

export function isLocalDateInRange(value, startDate, endDate) {
  const due = startOfLocalDay(value);
  const start = startOfLocalDay(startDate);
  const end = endOfLocalDay(endDate);
  if (!due || !start || !end) return false;
  return due.getTime() >= start.getTime() && due.getTime() <= end.getTime();
}

export function getDueDateRangeBounds(startDate, endDate) {
  const start = startOfLocalDay(startDate);
  const end = endOfLocalDay(endDate);
  if (!start || !end) return null;
  return { startTime: start.getTime(), endTime: end.getTime() };
}

export function isDueDateInRange(dueDateValue, rangeBounds) {
  if (!rangeBounds) return true;
  const dueDate = startOfLocalDay(dueDateValue);
  if (!dueDate) return false;
  const dueTime = dueDate.getTime();
  return dueTime >= rangeBounds.startTime && dueTime <= rangeBounds.endTime;
}

/**
 * Flatten report → legislation → compliance and apply global filters
 * at the compliance-row level (same idea as Compliance Action list/export).
 */
export function flattenDashboardCompliances(reportData, filters = {}) {
  const {
    startDate = null,
    endDate = null,
    companies = [],
    locations = [],
    applyDateFilter = true,
  } = filters;

  const rangeBounds =
    applyDateFilter && startDate && endDate
      ? getDueDateRangeBounds(startDate, endDate)
      : null;

  const result = [];

  (reportData || []).forEach((report) => {
    const reportAuditorId =
      report?.assigned_auditor_id ?? report?.assignedAuditorId ?? null;
    const reportApproverId =
      report?.assigned_approver_id ?? report?.assignedApproverId ?? null;
    const reportComplianceOfficerId =
      report?.assigned_compliance_officer_id ??
      report?.assignedComplianceOfficerId ??
      null;

    (report.legislation || []).forEach((leg) => {
      const companyName = leg?.company_name;
      const location = leg?.location;

      if (companies.length > 0 && !companies.includes(companyName)) {
        return;
      }
      if (locations.length > 0 && !locations.includes(location)) {
        return;
      }

      const legAuditorId =
        leg?.assigned_auditor_id ?? leg?.assignedAuditorId ?? null;
      const legApproverId =
        leg?.assigned_approver_id ?? leg?.assignedApproverId ?? null;
      const legComplianceOfficerId =
        leg?.assigned_compliance_officer_id ??
        leg?.assignedComplianceOfficerId ??
        null;

      (leg.compliance || []).forEach((comp) => {
        if (!isDueDateInRange(comp?.due_date, rangeBounds)) {
          return;
        }

        result.push({
          ...comp,
          current_compliance_status: normalizeComplianceStatus(
            comp?.current_compliance_status
          ),
          company_name: comp?.company_name ?? companyName,
          location: comp?.location ?? location,
          branch_id: comp?.branch_id ?? leg?.branch_id,
          assigned_auditor_id:
            comp?.assigned_auditor_id ?? legAuditorId ?? reportAuditorId,
          assigned_approver_id:
            comp?.assigned_approver_id ?? legApproverId ?? reportApproverId,
          assigned_compliance_officer_id:
            comp?.assigned_compliance_officer_id ??
            legComplianceOfficerId ??
            reportComplianceOfficerId,
        });
      });
    });
  });

  return result;
}

export function filterCompliancesByDays(data, days) {
  if (!days || isNaN(days) || Number(days) === 0) {
    return data || [];
  }

  const now = new Date();
  now.setHours(23, 59, 59, 999);
  const filterDate = new Date(now);
  filterDate.setDate(filterDate.getDate() - Number(days));
  filterDate.setHours(0, 0, 0, 0);

  return (data || []).filter((item) => {
    if (!item.due_date) return false;
    const dueDate = startOfLocalDay(item.due_date);
    if (!dueDate) return false;
    return dueDate >= filterDate && dueDate <= now;
  });
}

export function countCompliancesByStatus(
  items,
  statusKeys = COMPLIANCE_STATUS_KEYS
) {
  const counts = Object.fromEntries(statusKeys.map((key) => [key, 0]));

  (items || []).forEach((item) => {
    const status = normalizeComplianceStatus(item.current_compliance_status);
    if (counts[status] !== undefined) {
      counts[status] += 1;
    }
  });

  return counts;
}

export function sumBarChartComplianceStatuses(items) {
  // Total for categories widget = all filtered compliance rows
  return (items || []).length;
}

/**
 * Prune report tree so each legislation only keeps compliances that
 * pass the same global filters used for dashboard counts.
 */
export function filterReportTreeByGlobalFilters(reportData, filters = {}) {
  const flat = flattenDashboardCompliances(reportData, filters);
  const idSet = new Set(flat.map((item) => item.id));
  const companies = filters.companies || [];
  const locations = filters.locations || [];

  return (reportData || [])
    .map((report) => ({
      ...report,
      legislation: (report.legislation || [])
        .map((leg) => {
          if (companies.length > 0 && !companies.includes(leg.company_name)) {
            return { ...leg, compliance: [] };
          }
          if (locations.length > 0 && !locations.includes(leg.location)) {
            return { ...leg, compliance: [] };
          }
          return {
            ...leg,
            compliance: (leg.compliance || []).filter((comp) =>
              idSet.has(comp.id)
            ),
          };
        })
        .filter((leg) => (leg.compliance || []).length > 0),
    }))
    .filter((report) => (report.legislation || []).length > 0);
}
